import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { settingsApi, type NavModule } from '../services/settings';
import { billingApi, type CurrentPlan } from '../services/billing';

export function AppLayout() {
  const { user, logout, loading, canRead } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navModules, setNavModules] = useState<NavModule[]>([]);
  const [currentPlan, setCurrentPlan] = useState<CurrentPlan | null>(null);
  const isHoneyroomKiosk = location.pathname === '/honeyroom';

  const menuModules = navModules.filter((m) => m.moduleKey !== 'settings');

  const loadNavModules = () => {
    settingsApi.getNavModules()
      .then(setNavModules)
      .catch(() => setNavModules([]));
  };

  useEffect(() => {
    if (loading || !user) return;
    loadNavModules();
    billingApi.getCurrentPlan().then(setCurrentPlan).catch(() => setCurrentPlan(null));
  }, [loading, user]);

  useEffect(() => {
    const handler = () => {
      if (!loading && user) {
        loadNavModules();
      }
    };
    window.addEventListener('apisai:nav-modules-changed', handler);
    return () => window.removeEventListener('apisai:nav-modules-changed', handler);
  }, [loading, user]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.info('Utloggad');
    } catch {
      toast.error('Kunde inte logga ut mot servern, lokal session avslutad');
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className={isHoneyroomKiosk ? 'h-screen bg-black' : 'min-h-screen bg-white'}>
      {!isHoneyroomKiosk && (
      <header className="fixed inset-x-0 top-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between h-12 px-3">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="md:hidden p-1 rounded hover:bg-gray-100"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Meny"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logga.png" alt="ApisAI" className="h-8 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }} />
              <span className="hidden text-lg font-semibold text-primary">ApisAI</span>
            </Link>
            <nav className="hidden md:flex items-center gap-2">
              <Link to="/dashboard" className="px-2 py-1 text-sm text-gray-700 hover:text-primary hover:bg-orange-50 rounded">Hem</Link>
              {menuModules.map(({ path, label, moduleKey }) => (
                <Link key={moduleKey} to={path} className="px-2 py-1 text-sm text-gray-700 hover:text-primary hover:bg-orange-50 rounded">
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link to="/account-delete" className="hidden md:inline-block px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded">Konto</Link>
                {canRead('settings') && (
                  <Link to="/settings" className="hidden md:inline-flex p-1.5 text-gray-700 hover:text-primary hover:bg-orange-50 rounded" aria-label="Inställningar" title="Inställningar">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.983 5.5c.455-1.86 3.096-1.86 3.551 0 .253 1.034 1.436 1.526 2.358.969 1.659-1.002 3.527.866 2.525 2.525-.557.922-.065 2.105.969 2.358 1.86.455 1.86 3.096 0 3.551-1.034.253-1.526 1.436-.969 2.358 1.002 1.659-.866 3.527-2.525 2.525-.922-.557-2.105-.065-2.358.969-.455 1.86-3.096 1.86-3.551 0-.253-1.034-1.436-1.526-2.358-.969-1.659 1.002-3.527-.866-2.525-2.525.557-.922.065-2.105-.969-2.358-1.86-.455-1.86-3.096 0-3.551 1.034-.253 1.526-1.436.969-2.358-1.002-1.659.866-3.527 2.525-2.525.922.557 2.105.065 2.358-.969Z" />
                      <circle cx="13.75" cy="13.75" r="2.25" strokeWidth={2} />
                    </svg>
                  </Link>
                )}

                <div className="text-right leading-tight">
                  <div className="text-xs md:text-sm text-gray-700">{user.displayName}</div>
                  <div className="text-[11px] md:text-xs text-primary flex items-center justify-end gap-1">
                    {user.isSuperAdmin && (
                      <Link to="/superadmin" className="hover:underline">
                        Superadmin
                      </Link>
                    )}
                    {user.isSuperAdmin && user.isOwner && currentPlan && <span className="text-gray-400">•</span>}
                    {user.isOwner && currentPlan && (
                      <Link to="/settings/plan" className="hover:underline">
                        {currentPlan.planName}
                      </Link>
                    )}
                  </div>
                  {!user.isOwner && user.businessName && (
                    <div className="text-xs text-gray-500">{user.businessName}</div>
                  )}
                </div>

                <button type="button" onClick={handleLogout} className="hidden md:inline-flex btn btn-primary px-2 py-1">
                  Logga ut
                </button>
              </>
            ) : (
              <Link to="/login" className="btn btn-primary px-2 py-1">Logga in</Link>
            )}
          </div>
        </div>

        {currentPlan && currentPlan.daysLeft <= 30 && (
          <div className="border-t border-orange-200 bg-orange-50 px-3 py-1.5 text-xs text-orange-900 flex items-center justify-between">
            <span>
              Din plan går ut snart ({currentPlan.daysLeft} dagar kvar)
              {currentPlan.daysLeft <= 2 ? ' - akut förnyelse behövs.' : currentPlan.daysLeft <= 7 ? ' - förnya inom kort.' : ' - planera förnyelse.'}
            </span>
            <Link to="/settings/plan" className="text-primary hover:underline">Förnya</Link>
          </div>
        )}

        {menuOpen && (
          <div className="md:hidden border-t border-orange-200 border-b-4 border-b-orange-300 bg-orange-50/90 shadow-sm py-2">
            {menuModules.map(({ path, label, moduleKey }) => (
              <Link key={moduleKey} to={path} className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setMenuOpen(false)}>
                {label}
              </Link>
            ))}
            {user && (
              <>
                <hr className="my-2 border-gray-200" />
                {canRead('settings') && (
                  <Link to="/settings" className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setMenuOpen(false)}>
                    Inställningar
                  </Link>
                )}
                <Link to="/account-delete" className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setMenuOpen(false)}>
                  Konto
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    void handleLogout();
                  }}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Logga ut
                </button>
              </>
            )}
          </div>
        )}
      </header>
      )}

      <main className={isHoneyroomKiosk ? 'h-screen bg-black' : 'p-3 pt-16'}>
        <Outlet />
      </main>
    </div>
  );
}
